import { useState } from 'react';

import { Heading } from '@/components/heading';
import { Link } from '@/components/link';
import { MiniMap } from '@/components/modules/mini-map';
import { Text } from '@/components/text';
import { useSavedItems } from '@/hooks/use-saved-items';
import { MapMarker } from '@/types';
import { tv } from '@/utils/styles';

import { DashboardMyShipments } from '../my-shipments';

interface DashboardMiniMapProps {
  hasTracking: boolean;
}

const DashboardMiniMap = ({ hasTracking }: DashboardMiniMapProps) => {
  const [mapMarkers, setMapMarkers] = useState<MapMarker[]>([]);
  const { savedItems, deleteSavedItem, createSavedItem } = useSavedItems();

  const handleMarkerChange = (markers: MapMarker[]) => {
    setMapMarkers(markers);
  };

  const { base, grid, map, noResults, noResultsTitle, noResultsMessage } = styles();

  return (
    <div className={base()}>
      <div className={grid()}>
        <div className={map({ className: 'chromatic-ignore' })}>
          <MiniMap paths={[]} markers={mapMarkers} options={{ maxZoom: 5, gestureHandling: 'cooperative' }} />
        </div>
        <div>
          {hasTracking ? (
            <DashboardMyShipments
              savedItems={savedItems}
              onDeleteSavedItem={deleteSavedItem}
              onCreateSavedItem={createSavedItem}
              onMarkerChange={handleMarkerChange}
            />
          ) : (
            <div className={noResults()}>
              <Heading variant="h4" as="h2" className={noResultsTitle()}>
                Shipment tracking not available
              </Heading>
              <Text className={noResultsMessage()}>
                Tracking is not available with your current plan. Unlock tracking and other advanced features by
                upgrading your <Link href="/plans">plan</Link> today.
              </Text>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const styles = tv({
  slots: {
    base: 'rounded-lg border border-grey-200 bg-white p-4',
    grid: 'grid grid-cols-1 gap-6 md:h-[500px] md:grid-cols-2',
    map: 'h-[30vh] overflow-hidden rounded-lg md:h-full',
    noResults: 'flex h-full flex-col items-center justify-center',
    noResultsTitle: 'mb-2 text-md font-semibold',
    noResultsMessage: 'mb-0 max-w-md text-center text-baseSm text-grey-500',
  },
});

export { DashboardMiniMap };
